import {
  PROJECT_STEP_STATUS_TYPE_ENUM,
  PROJECT_STEP_PRIORITY_TYPE_ENUM,
  PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM,
} from 'enums';
import {
  Box,
  Button,
  Dialog,
  Backdrop,
  MenuItem,
  useTheme,
  TextField,
  FormLabel,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { Project, User } from 'library/types';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCallback, useEffect, useState } from 'react';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';

type UpdateProjectStepFormParams = {
  title: string;
  description: string;
  relatedContactId: User | null;
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM;
  dueDate: Dayjs | null;
  notes: string;
  status: PROJECT_STEP_STATUS_TYPE_ENUM;
  estimatedTaskDay: string;
  alternativeRelatedContactId: User | null;
  responsibleContactType: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM;
  additionalEffortDay: string;
  responsibleCustomerFullName: string;
};

export type UpdateProjectStepPostFormParams = Omit<
  UpdateProjectStepFormParams,
  'estimatedTaskDay' | 'alternativeRelatedContactId' | 'additionalEffortDay' | 'relatedContactId' | 'dueDate'
> & {
  id: number;
  relatedContactId: number;
  estimatedTaskDay: number;
  alternativeRelatedContactId: number | null;
  additionalEffortDay: number;
  dueDate: string;
};

const defaultValues: UpdateProjectStepFormParams = {
  title: '',
  description: '',
  relatedContactId: null,
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
  dueDate: null,
  notes: '',
  status: PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED,
  estimatedTaskDay: '',
  alternativeRelatedContactId: null,
  responsibleContactType: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
  additionalEffortDay: '',
  responsibleCustomerFullName: '',
};

type UpdateProjectStepDialogProps = {
  open: boolean;
  handleClose: () => void;
  projectStepId: number | null;
  books: Book[];
  products: Project[];
  users: User[];
  priorityOptions: {
    value: PROJECT_STEP_PRIORITY_TYPE_ENUM;
    label: string | undefined;
  }[];
  statusOptions: {
    value: PROJECT_STEP_STATUS_TYPE_ENUM;
    label: string | undefined;
  }[];
  responsibleContactOptions: {
    value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM;
    label: string | undefined;
  }[];
  refresh: () => Promise<void>;
};

const UpdateProjectStepDialog = ({
  open,
  handleClose: closeDialog,
  projectStepId,
  books,
  products,
  users,
  priorityOptions,
  statusOptions,
  responsibleContactOptions,
  refresh,
}: UpdateProjectStepDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState<Book | null>(null);
  const [product, setProduct] = useState<Project | null>(null);

  // form validations & form state
  const schema = yup
    .object({
      title: yup
        .string()
        .trim()
        .max(
          50,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 50 }
          )
        )
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      description: yup
        .string()
        .trim()
        .max(
          250,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 250 }
          )
        )
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      relatedContactId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      dueDate: yup
        .mixed<Dayjs>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .test(
          'is-dayjs',
          intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
          (v) => dayjs.isDayjs(v) && v.isValid()
        ),
      notes: yup
        .string()
        .trim()
        .max(
          250,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 250 }
          )
        )
        .notRequired(),
      status: yup
        .number()
        .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      estimatedTaskDay: yup
        .string()
        .trim()
        .notRequired()
        .matches(/^(?:0\.[0-9]*[1-9][0-9]*|[1-9][0-9]*(?:\.[0-9]+)?)$/, {
          message: intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          }),
          excludeEmptyString: true,
        }),
      alternativeRelatedContactId: yup.mixed().notRequired(),
      responsibleContactType: yup
        .mixed<PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      responsibleCustomerFullName: yup
        .string()
        .trim()
        .when('responsibleContactType', {
          is: (value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM) =>
            value === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER ||
            value === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON,
          then: (schema) =>
            schema.required(
              intl.formatMessage({
                id: 'validations.required',
                defaultMessage: 'Bu alan boş bırakılamaz',
              })
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      additionalEffortDay: yup
        .string()
        .trim()
        .notRequired()
        .matches(/^(?:0\.[0-9]*[1-9][0-9]*|[1-9][0-9]*(?:\.[0-9]+)?)$/, {
          message: intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          }),
          excludeEmptyString: true,
        }),
    })
    .required();

  const { reset, watch, control, setValue, handleSubmit } = useForm<UpdateProjectStepFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // dynamic variables
  const watchResponsibleContactType = watch('responsibleContactType');

  // sync helper functions
  const handleClose = () => {
    closeDialog();

    setBook(null);
    setProduct(null);
    reset();
  };

  // async helper functions
  const onSubmit = async (data: UpdateProjectStepFormParams) => {
    if (!projectStepId || !data.relatedContactId || !data.dueDate) return;

    const postData: UpdateProjectStepPostFormParams = {
      ...data,
      responsibleCustomerFullName:
        data.responsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON
          ? ''
          : data.responsibleCustomerFullName,
      relatedContactId: data.relatedContactId.id,
      dueDate: data.dueDate.toISOString(),
      estimatedTaskDay: Number(data.estimatedTaskDay),
      alternativeRelatedContactId: data.alternativeRelatedContactId?.id ?? null,
      additionalEffortDay: Number(data.additionalEffortDay),

      id: projectStepId,
    };

    try {
      setLoading(true);

      const response = await apiService.updateProjectStep(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="projectStepManaging.updateProjectStepCompleted"
            defaultMessage="Proje Adımı güncelleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSpecificProjectStep = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificProjectStep(projectStepId as number);

      if (response.isSuccess) {
        const book = books.find((book) => book.id === response.data.bookId);

        if (book) {
          setBook(book);
        }

        const product = products.find((product) => product.id === response.data.projectId);

        if (product) {
          setProduct(product);
        }

        const user = users.find((user) => user.id === response.data.relatedContactId);

        if (user) {
          setValue('relatedContactId', user);
        }

        const backupUser = users.find((user) => user.id === response.data.alternativeRelatedContactId);

        if (backupUser) {
          setValue('alternativeRelatedContactId', backupUser);
        }

        setValue('title', response.data.title);
        setValue('description', response.data.description);
        setValue('priority', response.data.priority);
        setValue('dueDate', response.data.dueDate ? dayjs(response.data.dueDate) : null);
        setValue('notes', response.data.notes);
        setValue('status', response.data.status);
        setValue('estimatedTaskDay', response.data.estimatedTaskDay ? String(response.data.estimatedTaskDay) : '');
        setValue('responsibleContactType', response.data.responsibleContactType);
        setValue('responsibleCustomerFullName', response.data.responsibleCustomerFullName ?? '');
        setValue(
          'additionalEffortDay',
          response.data.additionalEffortDay ? String(response.data.additionalEffortDay) : ''
        );
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [projectStepId, setValue, books, products, users]);

  // useEffect hooks
  useEffect(() => {
    if (open && projectStepId) {
      getSpecificProjectStep();
    }
  }, [open, projectStepId, getSpecificProjectStep]);

  useEffect(() => {
    if (watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON) {
      setValue('responsibleCustomerFullName', '');
    }
  }, [watchResponsibleContactType, setValue]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="projectStepManaging.updateProjectStep" defaultMessage="Proje Adımı Güncelle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="projectStepManaging.updateProjectStepText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili Proje Adımı için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="bookId">
                <FormattedMessage id="effortTracing.bookId" defaultMessage="Proje Seçimi" />
              </FormLabel>
              <TextField fullWidth size="small" value={book?.companyName ?? ''} disabled />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="projectId">
                <FormattedMessage id="effortTracing.projectId" defaultMessage="İlgili Ürün" />
              </FormLabel>
              <TextField fullWidth size="small" value={product?.title ?? ''} disabled />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="priority">
                <FormattedMessage id="projectStepManaging.priority" defaultMessage="Öncelik" />
              </FormLabel>
              <Controller
                name="priority"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {priorityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="status">
                <FormattedMessage id="effortTracing.status" defaultMessage="Statü" />
              </FormLabel>
              <Controller
                name="status"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON ? 4 : 3,
              }}>
              <FormLabel htmlFor="relatedContactId">
                <FormattedMessage id="projectStepManaging.relatedContact" defaultMessage="İlgili Kişisi" />
              </FormLabel>
              <Controller
                name="relatedContactId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={users}
                    value={value}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: User | null) => onChange(newValue)}
                    getOptionLabel={(option: User) => option.nameSurname ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON ? 4 : 3,
              }}>
              <FormLabel htmlFor="alternativeRelatedContactId">
                <FormattedMessage
                  id="projectStepManaging.alternativeRelatedContact"
                  defaultMessage="Yedek İlgili Kişisi"
                />
              </FormLabel>
              <Controller
                name="alternativeRelatedContactId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={users}
                    value={value}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: User | null) => onChange(newValue)}
                    getOptionLabel={(option: User) => option.nameSurname ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: watchResponsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON ? 4 : 3,
              }}>
              <FormLabel htmlFor="responsibleContactType">
                <FormattedMessage id="productProjectPlan.responsibleContact" defaultMessage="Sorumlu" />
              </FormLabel>
              <Controller
                name="responsibleContactType"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}>
                    {responsibleContactOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {watchResponsibleContactType !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON && (
              <Grid size={{ xs: 12, md: 3 }}>
                <FormLabel htmlFor="responsibleCustomerFullName">
                  <FormattedMessage
                    id="projectStepManaging.responsibleCustomerFullName"
                    defaultMessage="Müşteri Sorumlu Ad Soyad"
                  />
                </FormLabel>
                <Controller
                  name="responsibleCustomerFullName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="estimatedTaskDay">
                <FormattedMessage id="productProjectPlan.estimatedTaskDay" defaultMessage="Tahmini Adım Süresi (gün)" />
              </FormLabel>
              <Controller
                name="estimatedTaskDay"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="additionalEffortDay">
                <FormattedMessage id="projectStepManaging.additionalEffortDay" defaultMessage="Ek Efor Süresi (gün)" />
              </FormLabel>
              <Controller
                name="additionalEffortDay"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="dueDate">
                <FormattedMessage id="projectStepManaging.terminateDate" defaultMessage="Termin" />
              </FormLabel>
              <Controller
                name="dueDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    disablePast
                    localeText={renderDatePickerLocaleText()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="title">
                <FormattedMessage id="projectStepManaging.projectStep" defaultMessage="Proje Adımı" />
              </FormLabel>
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="description">
                <FormattedMessage id="projectStepManaging.stepExplanation" defaultMessage="Adım Açıklaması" />
              </FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormLabel htmlFor="notes">
                <FormattedMessage id="projectStepManaging.notes" defaultMessage="Notlar" />
              </FormLabel>
              <Controller
                name="notes"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={8}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default UpdateProjectStepDialog;
